import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { preserveCursorPosition } from "@/components/expression/utils/matrix";
import { Icon } from "@/components/icons/Icon";
import { NumberInput } from "@/components/sidebar/NumberInput";
import { ActiveTooltip, TooltipShortcut } from "@/components/tooltip/Tooltip";
import { displayHotkey } from "@/utils/hotkeys";

import type { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";
import type { MatrixBounds } from "@corca/data";

type Props = {
  node: MatrixNodeModel;
  hideRowsInput?: boolean;
  hideColumnsInput?: boolean;
  rowsInputLabel?: string;
  columnsInputLabel?: string;
};

export const MatrixDimensions = observer(function MatrixDimensions({
  node,
  hideRowsInput,
  hideColumnsInput,
  rowsInputLabel,
  columnsInputLabel,
}: Props) {
  const t = useTranslations();

  const rowsLabelText = rowsInputLabel || t("sidebar.actions.addRow");
  const columnsLabelText = columnsInputLabel || t("sidebar.actions.addColumn");

  const onBoundsChange = (bounds: MatrixBounds) => {
    preserveCursorPosition(node, () => {
      node.doc.transact(() => {
        node.setBounds(bounds);
      }, "matrix:MatrixDimensions");
    });
  };

  return (
    <div className="flex">
      {!hideRowsInput && (
        <ActiveTooltip
          focusDisabled
          title={
            <>
              {rowsLabelText}
              <TooltipShortcut>{displayHotkey("add-row")}</TooltipShortcut>
            </>
          }
        >
          <NumberInput
            value={node.rows}
            min={1}
            max={999}
            className="max-w-[76px] mr-2"
            icon={<Icon name="MatrixRows" />}
            aria-label={rowsLabelText}
            onSubmit={(value) =>
              onBoundsChange({
                ...node.bounds,
                rows: value,
              })
            }
          />
        </ActiveTooltip>
      )}
      {!hideColumnsInput && (
        <ActiveTooltip
          focusDisabled
          title={
            <>
              {columnsLabelText}
              <TooltipShortcut>{displayHotkey("add-column")}</TooltipShortcut>
            </>
          }
        >
          <NumberInput
            value={node.cols}
            min={1}
            max={999}
            className="max-w-[76px] mr-2"
            icon={<Icon name="MatrixCols" />}
            aria-label={columnsLabelText}
            onSubmit={(value) =>
              onBoundsChange({
                ...node.bounds,
                cols: value,
              })
            }
          />
        </ActiveTooltip>
      )}
    </div>
  );
});
